import React from 'react';
import { Link } from 'react-router-dom';
import FooterItem from 'src/components/app/footer/footer-item/FooterItem';
import * as AppStrings from 'src/localization/string-ids/app-strings';
import * as AppRoutes from 'src/routing/constants/routes';
import { UnloadPages } from 'src/store/common-slices/unloadSlice';
import './UnloadFooter.scss';

interface UnloadFooterProps {
    // A call is in flight on an unload step
    callPending: boolean;
    // The unload step on screen, which picks the items
    pageToRender: UnloadPages;
    // The loop is in a pallet, in staging
    inPallet: boolean;
    // Opens the orphan loop
    startOrphan: () => void;
    // Back from the own label step to the sideline ID step
    returnToSidelineId: () => void;
    // Ends the orphan loop
    endOrphan: () => void;
    // Opens or closes the sideline step
    showSideline: (open: boolean) => void;
    // Closes what the loop has bound
    closeBoundContainer: () => void;
}

interface UnloadActionProps {
    // Its data-testid
    testId: string;
    // The label under the icon
    text: string;
    // The icon's FooterItem name
    icon: string;
    // What a tap does
    onClick: () => void;
}

// An unload step's footer item acts on the page, opening a step or closing what is bound, so it is a button; the
// other modes' footer items are links to their routes
const UnloadAction = ({ testId, text, icon, onClick }: UnloadActionProps) => (
    <button type="button" data-testid={testId} onClick={onClick} className="unload-footer-action">
        <FooterItem footerItemText={text} footerItemTokenIdentifier={icon}/>
    </button>
);

// The unload pages' footer, in place of the tools: each step has its own items
class UnloadFooter extends React.Component<UnloadFooterProps> {
    private items() {
        switch (this.props.pageToRender) {
            case UnloadPages.SCAN_CONTAINER_PAGE:
                // A pallet closes as a trailer does, at a tap, and its loop takes no orphans
                return this.props.inPallet ? <>
                    <UnloadAction testId="unload-footer-close-pallet" text={AppStrings.UNLOAD_FOOTER_CLOSE_PALLET}
                                  icon="sign-out" onClick={() => this.props.closeBoundContainer()}/>
                    {this.sideline()}
                </> : <>
                    <UnloadAction testId="unload-footer-close-trailer" text={AppStrings.UNLOAD_FOOTER_CLOSE_TRAILER}
                                  icon="sign-out" onClick={() => this.props.closeBoundContainer()}/>
                    {this.sideline()}
                    <UnloadAction testId="unload-footer-orphan" text={AppStrings.UNLOAD_FOOTER_ORPHAN} icon="orphan"
                                  onClick={() => this.props.startOrphan()}/>
                </>;
            case UnloadPages.SCAN_ORPHAN_PAGE:
                return <UnloadAction testId="unload-footer-end-orphan" text={AppStrings.UNLOAD_FOOTER_END_ORPHAN}
                                     icon="sign-out" onClick={() => this.props.endOrphan()}/>;
            case UnloadPages.SCAN_SIDELINE_PAGE:
                return <UnloadAction testId="unload-footer-sideline-back" text={AppStrings.UNLOAD_FOOTER_BACK} icon="back"
                                     onClick={() => this.props.showSideline(false)}/>;
            case UnloadPages.SCAN_SIDELINE_TO_RESOLVE_PAGE:
                return this.back();
            case UnloadPages.SCAN_OWN_LABEL_PAGE:
                return <UnloadAction testId="unload-footer-own-label-back" text={AppStrings.UNLOAD_FOOTER_BACK} icon="back"
                                     onClick={() => this.props.returnToSidelineId()}/>;
            default:
                return <>
                    {this.back()}
                    <UnloadAction testId="unload-footer-orphan" text={AppStrings.UNLOAD_FOOTER_ORPHAN} icon="orphan"
                                  onClick={() => this.props.startOrphan()}/>
                </>;
        }
    }

    private sideline = () => (
        <UnloadAction testId="unload-footer-sideline" text={AppStrings.UNLOAD_FOOTER_SIDELINE} icon="sideline"
                      onClick={() => this.props.showSideline(true)}/>
    );

    // Back to the chooser, from the door, pallet and sideline ID steps
    private back = () => (
        <Link to={AppRoutes.UNLOAD_PAGE_ROUTE} data-testid="unload-footer-back" onClick={this.holdWhileBusy}>
            <FooterItem footerItemText={AppStrings.UNLOAD_FOOTER_BACK} footerItemTokenIdentifier="back"/>
        </Link>
    );

    // While a call is in flight Back goes nowhere, so its answer lands on the step that made it
    private holdWhileBusy = (event: React.MouseEvent) => {
        if (this.props.callPending) {
            event.preventDefault();
        }
    };

    // It takes the whole footer, so it carries the footer's own class and data-testid
    public render() {
        return <div className="footer-container unload-footer" data-testid="footer">{this.items()}</div>;
    }
}

export default UnloadFooter;
